import {Icon, Text} from 'platform/foundation';
import styled from 'styled-components';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {useOnMount} from 'shared/web';

import {ADVERTISEMENT_PREVIEW_SECTION_ICONS} from '../constants/ADVERTISEMENT_PREVIEW_SECTION_ICONS';
import {PREVIEW_CONTAINER_BREAKPOINTS} from '../constants/PREVIEW_CONTAINER_BREAKPOINTS';
import {PREVIEW_SECTION_IDS} from '../constants/PREVIEW_SECTION_IDS';
import {PREVIEW_STICKY_Z_INDEX} from '../constants/PREVIEW_STICKY_Z_INDEX';
import {getPreviewContainerBreakpoint} from '../utils/getPreviewContainerBreakpoint';

const SECTIONS = [
  {
    id: PREVIEW_SECTION_IDS.overview,
    label: 'page.advertisement.preview.overview',
    icon: ADVERTISEMENT_PREVIEW_SECTION_ICONS.overview,
  },
  {
    id: PREVIEW_SECTION_IDS.details,
    label: 'page.advertisement.preview.technicalData',
    icon: ADVERTISEMENT_PREVIEW_SECTION_ICONS.details,
  },
  {
    id: PREVIEW_SECTION_IDS.features,
    label: 'entity.vehicle.labels.vehicleEquipment',
    icon: ADVERTISEMENT_PREVIEW_SECTION_ICONS.features,
  },
  {
    id: PREVIEW_SECTION_IDS.description,
    label: 'entity.sourcing.vehiclePreview.labels.vehicleDescription',
    icon: ADVERTISEMENT_PREVIEW_SECTION_ICONS.description,
  },
];

function getScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (!isNil(parent)) {
    if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

export function AdvertisementPreviewSectionNav(props: {
  hasSpecifications: boolean;
  hasFeatures: boolean;
}) {
  const navRef = useRef<HTMLElement>(null);
  const scrollCleanupRef = useRef<() => void>(() => undefined);
  const [activeSection, setActiveSection] = useState<string>(PREVIEW_SECTION_IDS.overview);
  const sections = SECTIONS.filter(
    (section) =>
      (section.id !== PREVIEW_SECTION_IDS.details || props.hasSpecifications) &&
      (section.id !== PREVIEW_SECTION_IDS.features || props.hasFeatures)
  );

  useOnMount(
    () => {
      const nav = navRef.current;
      if (isNil(nav)) {
        return;
      }
      const scrollParent = getScrollParent(nav);
      if (isNil(scrollParent)) {
        return;
      }
      const updateActiveSection = () => {
        const present = SECTIONS.filter(
          (section) => !isNil(nav.parentElement?.querySelector(`#${section.id}`))
        );
        const reachedBottom =
          Math.ceil(scrollParent.scrollTop + scrollParent.clientHeight) >=
          scrollParent.scrollHeight;
        const threshold = nav.getBoundingClientRect().bottom;
        const visible = present.filter((section) => {
          const element = nav.parentElement?.querySelector(`#${section.id}`);
          return !isNil(element) && element.getBoundingClientRect().top <= threshold;
        });
        setActiveSection(
          (reachedBottom ? present : visible).at(-1)?.id ?? PREVIEW_SECTION_IDS.overview
        );
      };
      scrollParent.addEventListener('scroll', updateActiveSection, {passive: true});
      updateActiveSection();
      scrollCleanupRef.current = () =>
        scrollParent.removeEventListener('scroll', updateActiveSection);
    },
    () => scrollCleanupRef.current()
  );

  const scrollToSection = (id: string) => {
    const nav = navRef.current;
    const target = nav?.parentElement?.querySelector(`#${id}`);
    const scrollParent = isNil(nav) ? null : getScrollParent(nav);
    if (isNil(nav) || isNil(target) || isNil(scrollParent)) {
      return;
    }
    const stickyOffset =
      Number.parseFloat(getComputedStyle(nav).top) + nav.getBoundingClientRect().height;
    scrollParent.scrollTo({
      top:
        scrollParent.scrollTop +
        target.getBoundingClientRect().top -
        scrollParent.getBoundingClientRect().top -
        stickyOffset,
      behavior: 'smooth',
    });
    setActiveSection(id);
  };

  return (
    <Nav
      ref={navRef}
      $columnCount={sections.length}
      aria-label={i18n.t('entity.audits.checklist.sections')}
    >
      {sections.map((section) => (
        <NavButton
          key={section.id}
          type="button"
          aria-current={activeSection === section.id ? 'location' : undefined}
          onClick={() => scrollToSection(section.id)}
        >
          <Icon value={section.icon} size={4} isNotFilled />
          <RegularLabel>
            <Text inline color={activeSection === section.id ? 'link' : 'secondary'}>
              {i18n.t(section.label)}
            </Text>
          </RegularLabel>
          <CompactLabel>
            <Text inline size="xSmall" color={activeSection === section.id ? 'link' : 'secondary'}>
              {i18n.t(section.label)}
            </Text>
          </CompactLabel>
        </NavButton>
      ))}
    </Nav>
  );
}

const Nav = styled.nav<{$columnCount: number}>`
  position: sticky;
  top: ${({theme}) => theme.getSize(12)};
  z-index: ${PREVIEW_STICKY_Z_INDEX.nav};
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 0 ${({theme}) => theme.getSize(4)};
  padding: 0 ${({theme}) => theme.getSize(4)};
  border: 1px solid ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.palettes.white[10][100]};
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.medium
  )}) {
    display: grid;
    grid-template-columns: repeat(${({$columnCount}) => $columnCount}, minmax(0, 1fr));
    overflow-x: hidden;
    gap: 0;
    padding: 0;
  }
`;
const NavButton = styled.button`
  flex-shrink: 0;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  padding: ${({theme}) => theme.getSize(3)} ${({theme}) => theme.getSize(1)};
  color: ${({theme}) => theme.colors.text.secondary};
  border: 0;
  border-bottom: calc(${({theme}) => theme.getSize(1)} / 2) solid transparent;
  background: transparent;
  cursor: pointer;
  user-select: none;
  &:hover,
  &:focus-visible,
  &[aria-current='location'] {
    color: ${({theme}) => theme.colors.general.accent};
    border-bottom-color: currentColor;
  }
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.medium
  )}) {
    min-width: 0;
    white-space: normal;
    flex-direction: column;
    justify-content: center;
    gap: ${({theme}) => theme.getSize(1)};
    padding: ${({theme}) => theme.getSize(2)} ${({theme}) => theme.getSize(1)};
    text-align: center;
  }
`;
const RegularLabel = styled.div`
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.medium
  )}) {
    display: none;
  }
`;
const CompactLabel = styled.div`
  display: none;
  @container preview (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.medium
  )}) {
    display: block;
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;
